import type { components } from "./generated/api-schema";

export type Problem = components["schemas"]["ProblemDetail"];
export type Session = components["schemas"]["Session"];
export type SubmitOptions = components["schemas"]["SubmitOptions"];
export type Submission = components["schemas"]["Submission"];

export const API_URL = (import.meta.env.VITE_API_BASE_URL || "/api/v1").replace(
  /\/$/,
  "",
);
const apiOrigin = new URL(API_URL, window.location.origin).origin;
const legacyOrigin = import.meta.env.VITE_LEGACY_BASE_URL || apiOrigin;
export function legacyUrl(path = "/") {
  const url = new URL(path, legacyOrigin);
  return url.origin === new URL(legacyOrigin).origin
    ? url.href
    : new URL("/", legacyOrigin).href;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string,
    public legacyPath?: string,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  if (init.body) headers.set("Content-Type", "application/json");
  const response = await fetch(`${API_URL}${path}`, {
    ...init,
    headers,
    credentials: "include",
  });
  if (!response.headers.get("Content-Type")?.includes("application/json")) {
    throw new ApiError(
      "API không trả dữ liệu JSON. Kiểm tra kết nối hoặc đăng nhập lại.",
      response.status,
    );
  }
  const body = await response.json();
  if (!response.ok) {
    const detail =
      typeof body.error === "string" ? body.error : body.error?.message;
    throw new ApiError(
      detail || `Yêu cầu thất bại (${response.status})`,
      response.status,
      body.error?.code,
      body.legacy_url,
    );
  }
  return body as T;
}

async function post<T>(
  path: string,
  data: unknown = {},
  headers: Record<string, string> = {},
) {
  // Django rotates the CSRF cookie on login; obtain a matching token for every mutation.
  const { token } = await request<{ token: string }>("/auth/csrf");
  return request<T>(path, {
    method: "POST",
    headers: { ...headers, "X-CSRFToken": token },
    body: JSON.stringify(data),
  });
}

export const getSession = () => request<Session>("/auth/session");
export const login = (username: string, password: string) =>
  post<Session>("/auth/login", { username, password });
export const verifyTotp = (challenge: string, code: string) =>
  post<Session>("/auth/2fa/verify", { challenge, code });
export const logout = () => post<Session>("/auth/logout");
export const getProblems = (
  query: string,
  offset: number,
  signal?: AbortSignal,
) =>
  request<components["schemas"]["ProblemPage"]>(
    `/problems?${new URLSearchParams({ search: query, offset: String(offset), limit: "30" })}`,
    { signal },
  );
export const getProblem = (code: string) =>
  request<Problem>(`/problems/${encodeURIComponent(code)}`);
export const getSubmitOptions = (code: string) =>
  request<SubmitOptions>(
    `/problems/${encodeURIComponent(code)}/submit-options`,
  );
export const submitProblem = (
  code: string,
  source: string,
  language: string,
  key: string,
) =>
  post<Submission>(
    `/problems/${encodeURIComponent(code)}/submissions`,
    { source, language },
    { "Idempotency-Key": key },
  );
export const getSubmission = (id: string) =>
  request<Submission>(`/submissions/${id}`);
export const getSubmissions = (offset: number) =>
  request<components["schemas"]["SubmissionPage"]>(
    `/submissions?offset=${offset}&limit=25`,
  );
